import { type IDBPDatabase, openDB } from 'idb';

import type { DocStorageSchema } from './schema';

export const LOCAL_WORKSPACE_DATA_BUSY_ERROR = 'LocalWorkspaceDataBusyError';

/** Another tab or window still holds the local databases of the workspace. */
export class LocalWorkspaceDataBusyError extends Error {
  override readonly name = LOCAL_WORKSPACE_DATA_BUSY_ERROR;

  constructor() {
    super('Local workspace data is still open in another tab or window');
  }
}

/**
 * The recovery version a doc database was built on. null: it holds no
 * documents yet. 0: it holds documents but recorded no version.
 */
export async function readRecoveryVersion(
  db: IDBPDatabase<DocStorageSchema>
): Promise<number | null> {
  const tx = db.transaction(['publicationMeta', 'clocks'], 'readonly');
  const recorded = await tx
    .objectStore('publicationMeta')
    .get('recoveryVersion');
  if (recorded) return recorded.value;
  return (await tx.objectStore('clocks').count()) > 0 ? 0 : null;
}

async function localDatabaseNames(workspaceId: string): Promise<string[]> {
  const databases = (await indexedDB.databases?.()) ?? [];
  return databases
    .map(database => database.name)
    .filter((name): name is string => !!name && name.includes(workspaceId));
}

/**
 * The oldest recovery version among the local doc databases of a workspace;
 * null when none of them holds documents.
 */
export async function readLocalRecoveryVersion(
  workspaceId: string
): Promise<number | null> {
  let oldest: number | null = null;
  for (const name of await localDatabaseNames(workspaceId)) {
    const db = await openDB<DocStorageSchema>(name);
    try {
      if (
        !db.objectStoreNames.contains('publicationMeta') ||
        !db.objectStoreNames.contains('clocks')
      ) {
        continue;
      }
      const version = await readRecoveryVersion(db);
      if (version !== null) {
        oldest = oldest === null ? version : Math.min(oldest, version);
      }
    } finally {
      db.close();
    }
  }
  return oldest;
}

function deleteDatabase(name: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.deleteDatabase(name);
    request.onsuccess = () => resolve();
    request.onerror = () =>
      reject(request.error ?? new Error(`Deleting ${name} failed`));
    // A connection that stayed open keeps the deletion pending.
    request.onblocked = () => reject(new LocalWorkspaceDataBusyError());
  });
}

/** Deletes every local database of the workspace and waits for each. */
export async function deleteLocalDatabases(workspaceId: string): Promise<void> {
  for (const name of await localDatabaseNames(workspaceId)) {
    await deleteDatabase(name);
  }
}
